import React, { useEffect, useState } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import "../../../assets/css/SalesTrend/QuaterlyPerformance.css";
import { Box, CircularProgress } from "@mui/material";
import { useSalesContext } from "../../SalesContext";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const QuaterlyPerformance = ({ card2Data }) => {
  const { salesUnit, unit, loading,kpiData } = useSalesContext();
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 900);

  const defaultQtrPerformance = {
    Q1: {
      LYTD: { target_value: 0, actual_value: 0 },
      CYTD: { target_value: 0, actual_value: 0 },
    },
    Q2: {
      LYTD: { target_value: 0, actual_value: 0 },
      CYTD: { target_value: 0, actual_value: 0 },
    },
    Q3: {
      LYTD: { target_value: 0, actual_value: 0 },
      CYTD: { target_value: 0, actual_value: 0 },
    },
    Q4: {
      LYTD: { target_value: 0, actual_value: 0 },
      CYTD: { target_value: 0, actual_value: 0 },
    },
    Total: {
      LYTD: { target_value: 0, actual_value: 0 },
      CYTD: { target_value: 0, actual_value: 0 },
    },
  };

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 900);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (loading || !kpiData) {
    return (
      <Box
        sx={{
          p: 2,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const qtrPerformance = JSON.parse(
    JSON.stringify(card2Data || defaultQtrPerformance)
  );

  const fiscalYears = ["LYTD", "CYTD"];
  Object.keys(qtrPerformance).forEach((quarter) => {
    fiscalYears.forEach((fy) => {
      if (!qtrPerformance[quarter][fy]) {
        qtrPerformance[quarter][fy] = {
          target_value: 0,
          actual_value: 0,
          target_value_Qty: 0,
          actual_value_Qty: 0,
        };
      } else {
        qtrPerformance[quarter][fy].target_value_Qty =
          qtrPerformance[quarter][fy].target_value_Qty ?? 0;
        qtrPerformance[quarter][fy].actual_value_Qty =
          qtrPerformance[quarter][fy].actual_value_Qty ?? 0;
      }
    });
  });

  const displayLabels = fiscalYears.map((fy) => (fy === "LYTD" ? "LY" : fy));
  const groupedLabels = Object.keys(qtrPerformance).map((quarter) => ({
    parent: quarter,
    children: displayLabels,
  }));

  const labels = groupedLabels.flatMap((group) => group.children);

  const targetValues = [];
  const actualValues = [];

  const conversionFactor = salesUnit === "Crores" ? 10000000 : 100000;

  Object.keys(qtrPerformance).forEach((quarter) => {
    const quarterData = qtrPerformance[quarter];
    fiscalYears.forEach((fy) => {
      const targetKey = unit === "Units" ? "target_value_Qty" : "target_value";
      const actualKey = unit === "Units" ? "actual_value_Qty" : "actual_value";

      const targetValue = Number(
        Math.max(
          (quarterData[fy][targetKey] || 0) / conversionFactor,
          0
        ).toFixed(1)
      );
      const actualValue = Number(
        Math.max(
          (quarterData[fy][actualKey] || 0) / conversionFactor,
          0
        ).toFixed(1)
      );

      targetValues.push(targetValue);
      actualValues.push(actualValue);
    });
  });

  const chartHeight = 250;
  const maxBarHeightPx = 200;
  const maxDataValue = Math.max(...targetValues, ...actualValues, 0);
  const scaleFactor = maxBarHeightPx / chartHeight;
  const adjustedMaxValue =
    maxDataValue > 0 ? maxDataValue * scaleFactor * 1.5 : 1;

  const data = {
    labels,
    datasets: [
      {
        label: "Target Value",
        data: targetValues,
        backgroundColor: "#D3D3D3",
        borderColor: "#D3D3D3",
        borderWidth: 1,
        barThickness: isSmallScreen ? 20 : 56,
        order: 1,
      },
      {
        label: "Actual Value",
        data: actualValues,
        barThickness: isSmallScreen ? 15 : 34,
        backgroundColor: "lightblue",
        borderColor: "#1a80bb",
        borderWidth: 1,
        categoryPercentage: 0.2,
        order: 0,
      },
    ],
  };

  const parentLabelsPlugin = {
    id: "parentLabels",
    afterDraw: (chart) => {
      const { ctx, scales } = chart;
      const xAxis = scales.x;
      const yAxisBottom = scales.y.bottom;

      ctx.save();
      ctx.textAlign = "center";
      ctx.font = `bold ${isSmallScreen ? 10 : 14}px Arial`;
      ctx.fillStyle = "#1a80bb";
      ctx.letterSpacing = "0.15px";

      const topPadding = 20;
      const labelPosition = yAxisBottom + topPadding + 14;

      let xStart = xAxis.left;
      const totalWidth = xAxis.width;
      const groupWidth = totalWidth / groupedLabels.length;

      groupedLabels.forEach((group, index) => {
        const xMiddle = xStart + groupWidth / 2;
        ctx.fillText(group.parent, xMiddle, labelPosition);
        xStart += groupWidth;
      });

      ctx.restore();
    },
  };

  const valueLabelsPlugin = {
    id: "valueLabels",
    afterDatasetsDraw(chart) {
      const { ctx } = chart;
      const { datasets } = chart.data;

      ctx.save();
      ctx.font = `bold ${isSmallScreen ? 10 : 14}px Arial`;
      ctx.textAlign = "center";

      datasets.forEach((dataset, datasetIndex) => {
        const meta = chart.getDatasetMeta(datasetIndex);

        meta.data.forEach((bar, index) => {
          if (bar && bar.y !== null && dataset.data[index] !== null) {
            const value = dataset.data[index];
            const xPos = bar.x;

            if (dataset.label === "Target Value") {
              const yPos = bar.base - 10;
              ctx.fillStyle = "#000";
              ctx.fillText(value, xPos, yPos);
            } else if (dataset.label === "Actual Value") {
              const yPos = bar.y - 10;
              ctx.fillStyle = "#000";
              ctx.fillText(value, xPos, yPos);
            }
          }
        });
      });

      ctx.restore();
    },
  };

  const options = {
    maintainAspectRatio: false,
    responsive: true,
    layout: {
      padding: {
        bottom: 32,
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: {
          display: false,
        },
        border: {
          display: false,
        },
        ticks: {
          callback: (value, index) => {
            if (labels[index] === "") return "";
            return labels[index];
          },
          font: {
            weight: "700",
            size: isSmallScreen ? 10 : 12,
          },
          color: "#000000",
        },
      },
      y: {
        beginAtZero: true,
        display: false,
        max: adjustedMaxValue,
      },
    },
    plugins: {
      legend: {
        position: "top",
        align: "start",
        padding: {
          bottom: 20,
        },
        labels: {
          usePointStyle: false,
          boxWidth: 15,
          boxHeight: 15,
          padding: 10,
          font: {
            size: isSmallScreen ? 12 : 14,
            weight: "bold",
          },
          generateLabels: (chart) => {
            const datasets = chart.data.datasets;
            return datasets.map((dataset, i) => ({
              text: dataset.label,
              fillStyle: dataset.backgroundColor,
              strokeStyle: dataset.borderColor,
              lineWidth: dataset.borderWidth,
              hidden: !chart.isDatasetVisible(i),
              index: i,
              fontColor:
                dataset.label === "Target Value" ? "#808080" : "#1a80bb",
            }));
          },
        },
      },
      tooltip: {
        enabled: true,
        mode: "index",
        intersect: false,
        filter: (tooltipItem) => {
          return (
            tooltipItem &&
            tooltipItem.chart.data.labels[tooltipItem.dataIndex] !== ""
          );
        },
        callbacks: {
          title: (tooltipItems) => {
            if (!tooltipItems || !tooltipItems.length) {
              return "";
            }
            const index = tooltipItems[0].dataIndex;
            const label = tooltipItems[0].chart.data.labels[index];
            if (!label) return "";
            if (label === "LY") {
              return "LY";
            } else {
              return "CYTD";
            }
          },
          label: (tooltipItem) => {
            if (!tooltipItem) {
              return [];
            }
            const datasetLabel = tooltipItem.dataset.label;
            const index = tooltipItem.dataIndex;
            const value =
              tooltipItem.chart.data.datasets[tooltipItem.datasetIndex].data[
                index
              ];
            return `${datasetLabel}: ${value} ${salesUnit || "Lacs"}`;
          },
        },
      },
    },
    categoryPercentage: 0.6,
    barPercentage: 1.0,
  };

  return (
    <Box
      sx={{
        height: "355px",
        display: "flex",
        padding: "12px",
        alignItems: "flex-end",
        border: "1px solid #D9D9D9",
      }}
    >
      <Box sx={{ width: "100%", height: "345px" }}>
        <div className="chartBox">
          <Bar
            data={data}
            options={options}
            plugins={[parentLabelsPlugin, valueLabelsPlugin]}
          />
        </div>
      </Box>
    </Box>
  );
};

export default QuaterlyPerformance;
